import { memo, useEffect, useCallback, useState } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography, Grid2, Button } from "@mui/material";
import { getOwners, getLambScrapies, updateLambScrapie } from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import {
    lambSlugHeaderNoFilterable,
    damCodeHeaderNoClick,
    sireCodeHeaderNoClick,
    sexHeaderNoFilter,
    birthDateHeaderNotFilterable,
} from "../../components/TableHeaders.js";
import { dayjs } from "../../utils/index.js";

const UpdateLambScrapie = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOnwers, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [step, setStep] = useState(1);
    const [fetchedScrapies, setFetchedScrapies] = useState([]);
    const [selectedScrapie, setSelectedScrapie] = useState(null);
    const [selectedLamb, setSelectedLamb] = useState(null);
    const [scrapiesDropdownValues, setScrapiesDropdownValues] = useState([]);
    const [scrapieCds, setScrapieCds] = useState([]);
    const [scrapieCodesDropdownValue, setScrapieCodesDropdownValues] = useState([]);
    const [sampleStts, setSampleStts] = useState([]);
    const [sampleStatesDropdownValues, setSampleStatesDropdownValues] = useState([]);

    const selectClasses = useSelectStyles();
    const listClasses = useListStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(
                owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchData();
    }, []);


    const selectOwnerDatesFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "date-picker",
            id: "startLambingDate",
            type: "desktop",
            label: "Έναρξη περιόδου τοκετών",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "date-picker",
            id: "endLambingDate",
            type: "desktop",
            label: "Τέλος περιόδου τοκετών",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const selectLambFormContent = [
        {
            customType: "dropdown",
            id: "lamb",
            items: scrapiesDropdownValues,
            placeholder: "Επιλογή αμνού",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const scrapieLambFormContent = [
        {
            customType: "input",
            id: "sampleNumber",
            type: "text",
            placeholder: "Αριθμός Δείγματος",
            disabled: false,
            value: selectedScrapie?.sampleCode,
        },
        {
            customType: "dropdown",
            id: "sampleState",
            items: sampleStatesDropdownValues,
            placeholder: "Κατάσταση Δείγματος",
            required: false,
            defaultValue: sampleStts.find((state) => state.sampleStateCode === selectedScrapie?.sampleState)?.sampleStateName,
        },
        {
            customType: "dropdown",
            id: "firstResult",
            items: scrapieCodesDropdownValue,
            placeholder: "Πρώτο Αποτέλεσμα",
            required: true,
            defaultValue: scrapieCds.find((code) => Number(code.scrapieCode) === selectedScrapie?.result1)?.scrapieName,
        },
        {
            customType: "dropdown",
            id: "secondResult",
            items: scrapieCodesDropdownValue,
            placeholder: "Δεύτερο Αποτέλεσμα",
            required: true,
            defaultValue: scrapieCds.find((code) => Number(code.scrapieCode) === selectedScrapie?.result2)?.scrapieName,
        },
        {
            customType: "date-picker",
            id: "testDate",
            type: "desktop",
            label: "Ηερομηνία Δειγματοληψίας",
            width: "595px",
            background: "third",
            required: true,
            value: dayjs(selectedScrapie?.sampleDate),
        },
        {
            customType: "date-picker",
            id: "resultDate",
            type: "desktop",
            label: "Ημερομηνία Αποτελεσμάτων",
            width: "595px",
            background: "third",
            required: true,
            value: dayjs(selectedScrapie?.resultDate),
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const lambTableColumns = [
        lambSlugHeaderNoFilterable,
        damCodeHeaderNoClick,
        sireCodeHeaderNoClick,
        sexHeaderNoFilter,
        birthDateHeaderNotFilterable,
    ];

    const handleOwnerDateSelect = async (values) => {
        setIsLoading(true);
        const selectedOwnerObj = fetchedOnwers.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        setSelectedOwner(selectedOwnerObj);
        setSelectedStartDate(values.startLambingDate);
        setSelectedEndDate(values.endLambingDate);
        const { success: scs, scrapies, scrapieCodes, sampleStates, message } = await getLambScrapies(
            selectedOwnerObj?._id,
            values.startLambingDate,
            values.endLambingDate,
        );
        if (scs) {
            setFetchedScrapies(scrapies);
            setScrapieCds(scrapieCodes);
            setSampleStts(sampleStates);
            setScrapiesDropdownValues(
                scrapies.map((scrapie) => ({
                    value: scrapie?.lambSlug,
                    text: scrapie?.lambSlug,
                }))
            );
            setScrapieCodesDropdownValues(
                scrapieCodes.map((code) => ({
                    value: code.scrapieName,
                    text: code.scrapieName,
                }))
            );
            setSampleStatesDropdownValues(
                sampleStates.map((state) => ({
                    value: state.sampleStateName,
                    text: state.sampleStateName,
                }))
            );
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    };

    const handleScrapieSelect = (values) => {
        const selectedScrapieObj = fetchedScrapies.find(
            (scrapie) => `${scrapie?.lambSlug}` === values.lamb
        );
        setSelectedScrapie(selectedScrapieObj);
        setSelectedLamb(selectedScrapieObj.animalId);
        setStep(3);
    }

    const handleScrapieUpdate = async (values) => {
        setIsLoading(true);
        const scrapieId = selectedScrapie?._id;
        const ownerId = selectedOwner?._id;
        const animalId = selectedLamb?._id;
        const sampleCode = values.sampleNumber;
        const sampleDate = values.testDate;
        const resultDate = values.resultDate;
        const sampleState = sampleStts.find((state) => state.sampleStateName === values.sampleState)?.sampleStateCode;
        const result1 = Number(scrapieCds.find((code) => code.scrapieName === values.firstResult)?.scrapieCode);
        const result2 = Number(scrapieCds.find((code) => code.scrapieName === values.secondResult)?.scrapieCode);
        const { success: scs, message } = await updateLambScrapie(
            scrapieId,
            ownerId,
            animalId,
            sampleCode,
            sampleDate,
            resultDate,
            sampleState,
            result1,
            result2
        );
        if (scs) {
            success(message);
            const updatedScrapies = fetchedScrapies.map((scrapie) => {
                if (scrapie._id === scrapieId) {
                    return {
                        ...scrapie,
                        sampleCode: sampleCode,
                        sampleDate: sampleDate,
                        resultDate: resultDate,
                        sampleState: sampleState,
                        result1: result1,
                        result2: result2,
                    };
                }
                return scrapie;
            });
            setFetchedScrapies(updatedScrapies);
            setStep(2);
            setScrapiesDropdownValues(
                updatedScrapies.map((scrapie) => ({
                    value: scrapie?.lambSlug,
                    text: scrapie?.lambSlug,
                }))
            );
            setSelectedScrapie(null);
            setSelectedLamb(null);
            setScrapieCodesDropdownValues(
                scrapieCds.map((code) => ({
                    value: code.scrapieName,
                    text: code.scrapieName,
                }))
            );
            setSampleStatesDropdownValues(
                sampleStts.map((state) => ({
                    value: state.sampleStateName,
                    text: state.sampleStateName,
                }))
            );
            setSampleStts(sampleStts);
            setScrapieCds(scrapieCds);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή και τις ημερομηνίες που σας ενδιαφέρουν:
                        </Typography>
                        <Form
                            content={selectOwnerDatesFormContent}
                            onSubmit={handleOwnerDateSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            {`Ενημέρωση Στοιχείων SCARPIE για Αμνούς για την περίδο ${dayjs(selectedStartDate).format("DD/MM/YYYY")} - ${dayjs(selectedEndDate).format("DD/MM/YYYY")}`}
                        </Typography>
                        <Form
                            content={selectLambFormContent}
                            onSubmit={handleScrapieSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 3 && selectedScrapie && (
                <>
                    <Grid2
                        item
                        xs={12}
                        md={12}
                        sx={{
                            mt: "2rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6" className={listClasses.form_title} sx={{ display: "flex", justifyContent: "center" }}>
                            {"Στοιχεία Επιλεγμένου Αμνού"}
                        </Typography>
                        <Table
                            data={[selectedLamb]}
                            noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχει έλεγχος"}
                            columns={lambTableColumns}
                            showPageSizeOptions={false}
                        />
                    </Grid2>
                    <Box className={selectClasses.outerBox} sx={{ marginTop: "-5rem" }}>
                        <Box container flexDirection="column" className={selectClasses.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {`Ενημέρωση Στοιχείων SCARPIE για Αμνούς`}
                            </Typography>
                            <Form
                                content={scrapieLambFormContent}
                                onSubmit={handleScrapieUpdate}
                                toResetForm={true}
                            />
                        </Box>
                    </Box>
                </>
            )}
            {step !== 1 && (
                <Box sx={{ width: "100%", marginTop: step === 3 ? "-8rem" : "-13rem", display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" color="secondary" onClick={() => setStep(step - 1)}>
                        Πίσω
                    </Button>
                </Box>
            )}
        </>
    )
}

export default memo(UpdateLambScrapie);